import React, { useState, useEffect } from 'react';
import { getDatabase, ref, update, remove, push, set } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import './ProductCustomers.css';

const ProductCustomers = ({ product }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    // Bumped after a mutation to re-fetch the (no longer live) customer list.
    const [reloadKey, setReloadKey] = useState(0);

    const db = getDatabase();
    const auth = getAuth();

    // Load customers for this product
    useEffect(() => {
        if (product?.id && auth.currentUser) {
            setLoading(true);
            
            // Fetched server-side and scoped to this product's owner, so the
            // `purchases` node stays closed to clients. One-shot instead of a
            // live listener; `reloadKey` re-runs it after status changes.
            let cancelled = false;

            const loadCustomers = async () => {
                try {
                    const listCustomers = httpsCallable(functions, 'listProductCustomers');
                    const result = await listCustomers({ productId: product.id });
                    if (cancelled) return;

                    const purchasesData = Array.isArray(result?.data?.customers)
                        ? result.data.customers
                        : [];
                    console.log(`Found ${purchasesData.length} customers for product ${product.id}`);
                    setCustomers(purchasesData);
                } catch (error) {
                    if (cancelled) return;
                    console.error('Error loading purchases:', error?.code, error?.message);
                    setCustomers([]);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            };

            loadCustomers();

            return () => {
                cancelled = true;
            };
        } else {
            console.log('No product ID provided');
            setLoading(false);
        }
    }, [product?.id, reloadKey]);

    const handleStatusChange = async (customerId, newStatus) => {
        try {
            const purchaseRef = ref(db, `purchases/${customerId}`);
            await update(purchaseRef, { 
                status: newStatus,
                lastUpdated: Date.now()
            });

            // Update access table
            const customer = customers.find(c => c.id === customerId);
            if (customer) {
                const accessKey = `${customer.customerEmail}_${product.id}`;
                const accessRef = ref(db, `access/${accessKey}`);
                await update(accessRef, {
                    status: newStatus,
                    lastAccessed: Date.now()
                });
            }

            setReloadKey((k) => k + 1);
        } catch (error) {
            console.error('Error updating customer status:', error);
            alert('Error updating customer status. Please try again.');
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        if (window.confirm('Are you sure you want to delete this customer? This will remove their access to the product.')) {
            try {
                const purchaseRef = ref(db, `purchases/${customerId}`);
                await remove(purchaseRef);

                // Remove from access table
                const customer = customers.find(c => c.id === customerId);
                if (customer) {
                    const accessKey = `${customer.customerEmail}_${product.id}`;
                    const accessRef = ref(db, `access/${accessKey}`);
                    await remove(accessRef);
                }

                setReloadKey((k) => k + 1);
            } catch (error) {
                console.error('Error deleting customer:', error);
                alert('Error deleting customer. Please try again.');
            }
        }
    };

    const filteredCustomers = customers.filter(customer => {
        const matchesSearch = 
            customer.customerInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.customerInfo?.phone?.includes(searchTerm);
        
        const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown';
        return new Date(timestamp).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (amount, currency) => {
        if (amount === 0) return 'Free';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return '#10b981';
            case 'suspended':
                return '#f59e0b';
            case 'expired':
                return '#ef4444';
            case 'pending':
                return '#6366f1';
            default:
                return '#6b7280';
        }
    };

    const handleCustomerClick = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerModal(true);
    };

    const createTestCustomer = async () => {
        if (!product?.id) return;
        
        try {
            const testPurchase = {
                productId: product.id,
                providerId: product.providerId,
                customerEmail: 'test@example.com',
                customerInfo: {
                    name: 'Test Customer',
                    email: 'test@example.com',
                    phone: '+49 123 456789'
                },
                paymentType: 'free',
                amount: 0,
                currency: 'EUR',
                status: 'active',
                purchaseDate: Date.now()
            };

            const purchaseRef = push(ref(db, 'purchases'));
            await set(purchaseRef, testPurchase);

            setReloadKey((k) => k + 1);
            alert('Test customer created successfully!');
        } catch (error) {
            console.error('Error creating test customer:', error);
            alert('Error creating test customer: ' + error.message);
        }
    };

    if (loading) {
        return (
            <div className="sales-customers-loading">
                <div className="sales-loading-spinner"></div>
                <p>Loading customers...</p>
            </div>
        );
    }

    return (
        <div className="sales-product-customers">
            <div className="sales-customers-header">
                <div className="sales-customers-title">
                    <h3>Product Customers</h3>
                    <span className="sales-customers-count">{customers.length} total customers</span>
                </div>
                
                <div className="sales-customers-controls">
                    <div className="sales-search-box">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="sales-status-filter"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                        <option value="expired">Expired</option>
                    </select>
                </div>
            </div>

            {filteredCustomers.length === 0 ? (
                <div className="sales-customers-empty">
                    {customers.length === 0 ? (
                        <>
                            <div className="sales-empty-icon">🛒</div>
                            <h4>No Customers Yet</h4>
                            <p>This product hasn't been purchased yet. Once customers buy this product, they'll appear here with all their details.</p>
                            <div className="sales-empty-actions">
                                <div className="sales-empty-tip">
                                    <h5>💡 Get your first customers:</h5>
                                    <ul>
                                        <li>Integrate this product into your landing pages</li>
                                        <li>Share the product link on social media</li>
                                        <li>Set up email marketing campaigns</li>
                                        <li>Consider offering early bird pricing</li>
                                    </ul>
                                </div>
                                <div className="sales-empty-buttons">
                                    <button 
                                        className="sales-integration-guide-btn"
                                        onClick={() => {
                                            // TODO: Show integration guide or navigate to docs
                                            alert('Integration guide coming soon!');
                                        }}
                                    >
                                        📖 View Integration Guide
                                    </button>
                                    <button 
                                        className="sales-test-customer-btn"
                                        onClick={createTestCustomer}
                                    >
                                        🧪 Create Test Customer
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="sales-empty-icon">🔍</div>
                            <h4>No Results Found</h4>
                            <p>Try adjusting your search or filter criteria.</p>
                            <button 
                                className="sales-clear-filters-btn"
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('all');
                                }}
                            >
                                Clear Filters
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="sales-customers-table">
                    <div className="sales-table-header">
                        <div className="sales-header-cell customer">Customer</div>
                        <div className="sales-header-cell payment">Payment</div>
                        <div className="sales-header-cell status">Status</div>
                        <div className="sales-header-cell date">Purchase Date</div>
                        <div className="sales-header-cell actions">Actions</div>
                    </div>
                    
                    <div className="sales-table-body">
                        {filteredCustomers.map(customer => (
                            <div key={customer.id} className="sales-table-row" onClick={() => handleCustomerClick(customer)}>
                                <div className="sales-cell customer">
                                    <div className="sales-customer-info">
                                        <div className="sales-customer-avatar">
                                            {customer.customerInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                        <div className="sales-customer-details">
                                            <h6>{customer.customerInfo?.name || 'Unknown'}</h6>
                                            <p>{customer.customerEmail}</p>
                                            {customer.customerInfo?.phone && (
                                                <p className="sales-phone">{customer.customerInfo.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="sales-cell payment">
                                    <div className="sales-payment-info">
                                        <span className="sales-amount">
                                            {formatPrice(customer.amount, customer.currency)}
                                        </span>
                                        <span className={`sales-payment-type ${customer.paymentType}`}>
                                            {customer.paymentType === 'stripe' && '💳'}
                                            {customer.paymentType === 'cash' && '💵'}
                                            {customer.paymentType === 'free' && '🎁'}
                                            {customer.paymentType}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="sales-cell status">
                                    <select
                                        value={customer.status}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleStatusChange(customer.id, e.target.value);
                                        }}
                                        className="sales-status-select"
                                        style={{ '--status-color': getStatusColor(customer.status) }}
                                    >
                                        <option value="active">Active</option>
                                        <option value="suspended">Suspended</option>
                                        <option value="expired">Expired</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>
                                
                                <div className="sales-cell date">
                                    {formatDate(customer.purchaseDate)}
                                </div>
                                
                                <div className="sales-cell actions">
                                    <button 
                                        className="sales-action-btn view"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCustomerClick(customer);
                                        }}
                                        title="View Details"
                                    >
                                        👁️
                                    </button>
                                    <button 
                                        className="sales-action-btn delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCustomer(customer.id);
                                        }}
                                        title="Delete Customer"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Customer Detail Modal */}
            {showCustomerModal && selectedCustomer && (
                <div className="sales-customer-modal">
                    <div className="sales-modal-content">
                        <div className="sales-modal-header">
                            <h4>Customer Details</h4>
                            <button 
                                className="sales-close-btn"
                                onClick={() => setShowCustomerModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="sales-modal-body">
                            <div className="sales-customer-details-grid">
                                <div className="sales-detail-section">
                                    <h5>Customer Information</h5>
                                    <div className="sales-detail-item">
                                        <label>Name:</label>
                                        <span>{selectedCustomer.customerInfo?.name || 'Not provided'}</span>
                                    </div>
                                    <div className="sales-detail-item">
                                        <label>Email:</label>
                                        <span>{selectedCustomer.customerEmail}</span>
                                    </div>
                                    {selectedCustomer.customerInfo?.phone && (
                                        <div className="sales-detail-item">
                                            <label>Phone:</label>
                                            <span>{selectedCustomer.customerInfo.phone}</span>
                                        </div>
                                    )}
                                    {selectedCustomer.customerInfo?.company && (
                                        <div className="sales-detail-item">
                                            <label>Company:</label>
                                            <span>{selectedCustomer.customerInfo.company}</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="sales-detail-section">
                                    <h5>Purchase Information</h5>
                                    <div className="sales-detail-item">
                                        <label>Amount:</label>
                                        <span>{formatPrice(selectedCustomer.amount, selectedCustomer.currency)}</span>
                                    </div>
                                    <div className="sales-detail-item">
                                        <label>Payment Method:</label>
                                        <span className={`sales-payment-method ${selectedCustomer.paymentType}`}>
                                            {selectedCustomer.paymentType}
                                        </span>
                                    </div>
                                    <div className="sales-detail-item">
                                        <label>Purchase Date:</label>
                                        <span>{formatDate(selectedCustomer.purchaseDate)}</span>
                                    </div>
                                    <div className="sales-detail-item">
                                        <label>Status:</label>
                                        <span 
                                            className="sales-status-badge"
                                            style={{ '--status-color': getStatusColor(selectedCustomer.status) }}
                                        >
                                            {selectedCustomer.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Additional customer data */}
                            {selectedCustomer.customerInfo && Object.keys(selectedCustomer.customerInfo).length > 2 && (
                                <div className="sales-additional-data">
                                    <h5>Additional Information</h5>
                                    <div className="sales-additional-grid">
                                        {Object.entries(selectedCustomer.customerInfo).map(([key, value]) => {
                                            if (key === 'name' || key === 'email') return null;
                                            return (
                                                <div key={key} className="sales-detail-item">
                                                    <label>{key.charAt(0).toUpperCase() + key.slice(1)}:</label>
                                                    <span>{value}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="sales-modal-footer">
                            <button 
                                className="sales-modal-btn secondary"
                                onClick={() => setShowCustomerModal(false)}
                            >
                                Close
                            </button>
                            <button 
                                className="sales-modal-btn primary"
                                onClick={() => {
                                    // TODO: Export customer data or send email
                                    alert('Export functionality coming soon!');
                                }}
                            >
                                Export Data
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductCustomers;
